import { FaFireAlt, FaTrophy, FaBriefcase, FaRocket, FaShieldAlt, FaBox, FaExchangeAlt, FaTools, FaRobot } from "react-icons/fa";
import type { ReactNode } from "react";

/** Defines a single achievement or milestone with display metadata: category icon, title, description, date, and accent color. Used in the Achievements section. */
export interface Achievement {
  /** Category tag for grouping (e.g. "streak", "career", "optimization", "award", "stability", "infrastructure", "tooling", "ai") */
  type: string;
  /** React icon component rendered at the top of the achievement card */
  icon: ReactNode;
  /** Achievement headline */
  title: string;
  /** Detailed description with context and impact */
  description: string;
  /** Date or date range when the achievement was earned */
  date: string;
  /** CSS color string used for the card accent and icon theme */
  color: string;
}

/** Professional milestones spanning DSA streaks, career ownership, optimizations, awards, infrastructure wins, tooling, and AI work. Each entry represents a verified highlight from Mohit's career. */
export const achievements: Achievement[] = [
  /** Solo senior developer delivering 3 major systems in 6 months at Extramarks */
  {
    type: "career",
    icon: <FaBriefcase />,
    title: "Full Ownership Delivery - Extramarks",
    description:
      "Architected and shipped 3 major systems in 6 months as a Solo Senior Developer: a 3D Bird Sanctuary exploration game, a 2D spatial visualization pattern game, and internal React/web tools for teachers. Full ownership from concept to deployment.",
    date: "Jul - Dec 2025",
    color: "var(--accent-color)",
  },
  /** 146+ day DSA consistency streak achieved on Raj Vikramaditya's (Striver) platform */
  {
    type: "streak",
    icon: <FaFireAlt />,
    title: "146+ Days DSA Streak - takeUforward",
    description:
      "Maintained a 146+ day active streak solving Data Structures & Algorithms problems on Raj Vikramaditya's (Striver) platform.",
    date: "August 2025",
    color: "#ff5e00",
  },
  /** 90% WebGL build size reduction from 327MB to 32MB while retaining full visual fidelity */
  {
    type: "optimization",
    icon: <FaRocket />,
    title: "327MB \u2192 32MB: Bird Sanctuary Optimization",
    description:
      "Architected a 3D Bird Sanctuary exploration game in Unity with custom shaders and complex state machines. Optimized the WebGL build 327MB \u2192 32MB (90% reduction) while retaining full visual fidelity and performance.",
    date: "Jul - Dec 2025",
    color: "#f5576c",
  },
  /** Recognized as Top Performer for two consecutive cycles (2022-2023) at MadOverGames */
  {
    type: "award",
    icon: <FaTrophy />,
    title: "Top Performer Award x2 - MadOverGames",
    description:
      "Recognized as a Top Performer for two consecutive cycles (2022-2023) at MadOverGames, acknowledging exceptional dedication, quality of work, and impact on top-grossing titles (25M+ Downloads).",
    date: "2022 - 2023",
    color: "#ffd700",
  },
  /** Six Top Performer Excellence Certificates across core CS disciplines from Coding Ninjas */
  {
    type: "award",
    icon: <FaTrophy />,
    title: "6x Excellence Awards - Coding Ninjas",
    description:
      "Awarded 'Top Performer' and Excellence Certificates across 6 core disciplines: Full Stack Web Development, System Design, DBMS, Data Structures in C++, Java, and Introduction to C++ Programming.",
    date: "2019 - 2022",
    color: "#ffd700",
  },
  /** 99.85% crash-free session rate achieved through thread-safety and memory profiling */
  {
    type: "stability",
    icon: <FaShieldAlt />,
    title: "Enterprise-Grade Stability: 99.85% Crash-Free",
    description:
      "Led Bubble Shooter Pro to a 99.85% crash-free session rate through rigorous thread-safety fixes, memory profiling via Asset Bundles/Addressables, and Firebase Crashlytics-driven error resolution across the entire game lifecycle.",
    date: "2024 - 2025",
    color: "#38f9d7",
  },
  /** Further 8-9MB reduction on an already-optimized legacy game through systematic asset cleanup */
  {
    type: "optimization",
    icon: <FaBox />,
    title: "Legacy Game: 10MB Size Reduction",
    description:
      "Further optimized Bubble Shooter Pro, a 100MB game that had been live for years and was already well-optimized. Reduced its footprint by 10MB through systematic asset cleanup, sprite/audio compression, and dependency removal while maintaining existing asset quality standards.",
    date: "2024",
    color: "#f5576c",
  },
  /** Migrated all game titles from Google Analytics to Firebase Analytics for real-time tracking */
  {
    type: "infrastructure",
    icon: <FaExchangeAlt />,
    title: "Analytics Infrastructure Migration",
    description:
      "Replaced Google Analytics with Firebase Analytics across all game titles for real-time reporting, accurate tracking, and integrated Crashlytics error monitoring.",
    date: "2024",
    color: "#00f2fe",
  },
  /** In-engine debug tool with scene switching, date manipulation, and automated build pipelines */
  {
    type: "tooling",
    icon: <FaTools />,
    title: "Custom Developer Debug Console",
    description:
      "Built a debug tool with one-click scene changes, date-time manipulation, unlock-level scripts, long-press/single-tap detection, and automated build pipelines, saving hours of QA and developer time weekly.",
    date: "2023 - 2024",
    color: "#fbc2eb",
  },
  /** Game AI bot with intelligent pathfinding achieving 7-second average level completion */
  {
    type: "ai",
    icon: <FaRobot />,
    title: "AI Bot: 7-Second Level Completion",
    description:
      "Built a game bot that completes levels in 7 seconds on average in fast play mode. Uses pathfinding, Ghost Bubble targeting, and probability-based decision making for human-like play patterns.",
    date: "2024 - 2025",
    color: "#a18cd1",
  },
];
